import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Search, Building2, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

interface Contact {
  id: string;
  name: string;
  avatar?: string;
  type: "team" | "client" | "lead" | "prime-contractor" | "subcontractor" | "business-contact";
  email?: string;
  organizationId?: string;
  organizationName?: string;
}

interface NewMessageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateMessage: (contactId: string) => Promise<void>;
  contacts: Contact[];
}

export function NewMessageDialog({
  isOpen,
  onClose,
  onCreateMessage,
  contacts,
}: NewMessageDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedContactId, setSelectedContactId] = useState<string>("");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string>("");

  const filteredContacts = contacts.filter((contact) => {
    const query = searchQuery.toLowerCase();
    return (
      contact.name.toLowerCase().includes(query) ||
      (contact.email && contact.email.toLowerCase().includes(query)) ||
      (contact.organizationName && contact.organizationName.toLowerCase().includes(query)) ||
      contact.type.toLowerCase().includes(query)
    );
  });

  const handleCreateMessage = async () => {
    if (!selectedContactId) {
      setError("Please select a contact first.");
      return;
    }

    const contact = contacts.find((c) => c.id === selectedContactId);
    if (!contact) {
      setError("Selected contact not found. Please try again.");
      return;
    }

    // Validate cross-organization contacts have organizationId
    if (
      (contact.type === "prime-contractor" ||
        contact.type === "subcontractor" ||
        contact.type === "business-contact") &&
      !contact.organizationId
    ) {
      setError(
        `Cannot start conversation with ${contact.name}. Organization information is missing.`
      );
      return;
    }

    setIsCreating(true);
    setError("");

    try {
      await onCreateMessage(selectedContactId);
      resetForm();
      onClose();
      toast.success(`Started conversation with ${contact.name}`);
    } catch (error) {
      console.error("Error creating conversation:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create conversation. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const resetForm = () => {
    setSearchQuery("");
    setSelectedContactId("");
    setError("");
    setIsCreating(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-hidden sm:max-w-[650px]">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-xl font-semibold text-gray-900">
            Start New Conversation
          </DialogTitle>
          <p className="mt-1 text-sm text-gray-600">Choose a contact to start messaging with</p>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Select Contact</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-11 border-gray-300 pl-9 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
            {!searchQuery && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="mb-2 text-sm font-medium text-gray-600">💡 Search Tips:</p>
                <ul className="space-y-1 text-sm text-gray-500">
                  <li>• Type a name to find team members</li>
                  <li>• Search by email address</li>
                </ul>
              </div>
            )}
          </div>

          {searchQuery && (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <ScrollArea className="h-[320px]">
                <div className="divide-y divide-gray-100">
                  {filteredContacts.length > 0 ? (
                    filteredContacts.map((contact) => (
                      <div
                        key={contact.id}
                        className={`cursor-pointer p-4 transition-all duration-200 ${
                          selectedContactId === contact.id
                            ? "border-l-4 border-l-blue-500 bg-blue-50 shadow-sm"
                            : "border-l-4 border-l-transparent hover:bg-gray-50 hover:shadow-sm"
                        }`}
                        onClick={() => setSelectedContactId(contact.id)}
                      >
                        <div className="flex items-start gap-3">
                          <Avatar className="h-10 w-10 flex-shrink-0">
                            <AvatarImage src={contact.avatar} />
                            <AvatarFallback className="bg-blue-100 text-sm font-medium text-blue-600">
                              {contact.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <div className="mb-1 flex items-center gap-2">
                              <h3 className="truncate font-medium text-gray-900">{contact.name}</h3>
                              <Badge
                                variant={contact.type === "team" ? "default" : "secondary"}
                                className={`flex-shrink-0 text-xs ${
                                  contact.type === "team"
                                    ? "border-blue-200 bg-blue-100 text-blue-700"
                                    : contact.type === "client"
                                      ? "border-green-200 bg-green-100 text-green-700"
                                      : contact.type === "lead"
                                        ? "border-yellow-200 bg-yellow-100 text-yellow-700"
                                        : "border-purple-200 bg-purple-100 text-purple-700"
                                }`}
                              >
                                {contact.type === "team"
                                  ? "Team Member"
                                  : contact.type === "client"
                                    ? "Client"
                                    : contact.type === "lead"
                                      ? "Lead"
                                      : contact.type === "prime-contractor"
                                        ? "Prime Contractor"
                                        : contact.type === "subcontractor"
                                          ? "Subcontractor"
                                          : "Business Partner"}
                              </Badge>
                            </div>
                            <div className="space-y-1">
                              {contact.email && (
                                <p className="truncate text-sm text-gray-600">{contact.email}</p>
                              )}
                              {contact.organizationName && (
                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                  <Building2 className="h-3 w-3 flex-shrink-0" />
                                  <span className="truncate">
                                    {contact.type === "team"
                                      ? `${contact.organizationName} (Your Team)`
                                      : contact.organizationName}
                                  </span>
                                </div>
                              )}
                              {contact.organizationId &&
                                !contact.organizationName &&
                                contact.type !== "team" && (
                                  <div className="flex items-center gap-1 text-xs text-gray-500">
                                    <Building2 className="h-3 w-3 flex-shrink-0" />
                                    <span>External Organization</span>
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <Search className="mx-auto mb-3 h-8 w-8 text-gray-300" />
                      <p className="mb-1 font-medium">No contacts found</p>
                      <p className="text-sm">Try searching for:</p>
                      <ul className="mt-2 space-y-1 text-sm">
                        <li>• Team member names</li>
                        <li>• Email addresses</li>
                      </ul>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          )}

          {selectedContactId && (
            <div className="rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Avatar className="h-10 w-10 ring-2 ring-blue-200">
                    <AvatarImage
                      src={filteredContacts.find((c) => c.id === selectedContactId)?.avatar}
                    />
                    <AvatarFallback className="bg-blue-100 text-sm font-medium text-blue-600">
                      {filteredContacts
                        .find((c) => c.id === selectedContactId)
                        ?.name?.split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-white bg-green-500">
                    <span className="text-xs text-white">✓</span>
                  </div>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-blue-900">
                    {filteredContacts.find((c) => c.id === selectedContactId)?.name}
                  </p>
                  <p className="text-sm text-blue-700">Ready to start conversation</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                <div>
                  <p className="text-sm font-medium text-red-800">Error</p>
                  <p className="mt-1 text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 pt-6">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isCreating}
            className="min-w-[100px]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateMessage}
            disabled={!selectedContactId || isCreating}
            className="min-w-[160px] bg-blue-600 text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : selectedContactId ? (
              "Start Conversation"
            ) : (
              "Select Contact First"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
